import { BookingStateBadge } from '@Components/StorageStock/BookingStateBadge';
import { BookingTypeBadge } from '@Components/StorageStock/BookingTypeBadge';
import { JournalQuantity } from '@Components/StorageStock/JournalQuantity';
import {
    type ApiStorageTransaction,
    enumStorageTransactionStatusOptions,
    type EnumStorageTransactionType,
    enumStorageTransactionTypeOptions,
} from '@Model';
import { bookingState, bookingStateLabel } from '@Modules/StorageTransactions/StorageTransaction/cancellation';
import type { Column, ITableWithStoredSettingsProps } from '@synqo/frontend-component-library';
import type { ReactElement } from 'react';

export const initialStorageTransactionSorting: Readonly<
    ITableWithStoredSettingsProps<ApiStorageTransaction>['initialSorting']
> = {
    columnName: 'occurred_at',
    direction: 'desc',
};

/** The journal columns. A badge of a cancelled or counter booking calls `openRelated` with its row, `renderOrigin` fills the origin cell. */
// @ai-generated 2026-10-26T15:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const getStorageTransactionColumns = (
    openRelated: (booking: ApiStorageTransaction) => void,
    renderOrigin: (booking: ApiStorageTransaction) => string | ReactElement,
): Readonly<Column<ApiStorageTransaction>>[] => [
    { name: 'occurred_at', label: 'Zeitpunkt', type: 'datetime' },
    {
        name: 'type',
        label: 'Buchungsart',
        disableColumnSort: true,
        filterVariant: 'select',
        filterOptions: enumStorageTransactionTypeOptions,
        renderCell: ({ cellData }) => <BookingTypeBadge type={cellData as EnumStorageTransactionType} />,
    },
    { name: 'storage_location.display_string', label: 'Lagerort' },
    { name: 'material.display_string', label: 'Material' },
    { name: 'avv.display_string', label: 'AVV' },
    {
        name: 'quantity',
        label: 'Gewicht (t)',
        type: 'decimal',
        decimalPlaces: 3,
        size: 150,
        renderCell: ({ rowData }) => (
            <JournalQuantity value={rowData.quantity} struckThrough={bookingState(rowData) === 'cancelled'} />
        ),
    },
    {
        name: 'origin_order.display_string',
        label: 'Herkunft',
        disableColumnSort: true,
        disableColumnFilter: true,
        size: 180,
        meta: { noRowClick: true },
        renderCell: ({ rowData }) => renderOrigin(rowData),
    },
    { name: 'remarks', label: 'Bemerkung' },
    {
        name: 'transaction_status',
        label: 'Storno',
        size: 130,
        filterVariant: 'select',
        filterOptions: enumStorageTransactionStatusOptions,
        renderCell: ({ rowData }) => {
            const state = bookingState(rowData);
            return state === 'effective' ? (
                <></>
            ) : (
                <BookingStateBadge label={bookingStateLabel(state)} onOpen={() => openRelated(rowData)} />
            );
        },
    },
];
